// ============================================
// Vignette Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { VIGNETTE_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Vignette post-processing effect
 * Darkens corners of the image for a cinematic look
 */
export class VignetteEffect extends PostProcessEffect {
  readonly type = 'vignette';
  readonly displayName = 'Vignette';
  
  // Parameters
  vignetteIntensity = 0.4;
  smoothness = 0.4;
  roundness = 1.0;
  colorR = 0.0;
  colorG = 0.0;
  colorB = 0.0;
  centerX = 0.5;
  centerY = 0.5;
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = VIGNETTE_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'vignetteIntensity', type: 'float', min: 0, max: 1, default: 0.4, description: 'Vignette strength' },
      { name: 'smoothness', type: 'float', min: 0.001, max: 1, default: 0.4, description: 'Edge softness' },
      { name: 'roundness', type: 'float', min: 0, max: 2, default: 1.0, description: 'Shape roundness' },
      { name: 'colorR', type: 'float', min: 0, max: 1, default: 0.0, description: 'Vignette color R' },
      { name: 'colorG', type: 'float', min: 0, max: 1, default: 0.0, description: 'Vignette color G' },
      { name: 'colorB', type: 'float', min: 0, max: 1, default: 0.0, description: 'Vignette color B' },
      { name: 'centerX', type: 'float', min: 0, max: 1, default: 0.5, description: 'Center X' },
      { name: 'centerY', type: 'float', min: 0, max: 1, default: 0.5, description: 'Center Y' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'vignetteIntensity': return this.vignetteIntensity;
      case 'smoothness': return this.smoothness;
      case 'roundness': return this.roundness;
      case 'colorR': return this.colorR;
      case 'colorG': return this.colorG;
      case 'colorB': return this.colorB;
      case 'centerX': return this.centerX;
      case 'centerY': return this.centerY;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'vignetteIntensity': this.vignetteIntensity = value; break;
      // smoothness = 0 makes the shader's smoothstep edges equal (0.5 → 0.5) → NaN. The UI min is
      // 0.001, but reconcile()/deserialize() can push arbitrary values — clamp here too.
      case 'smoothness': this.smoothness = Math.max(0.001, value); break;
      case 'roundness': this.roundness = value; break;
      case 'colorR': this.colorR = value; break;
      case 'colorG': this.colorG = value; break;
      case 'colorB': this.colorB = value; break;
      case 'centerX': this.centerX = value; break;
      case 'centerY': this.centerY = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const vignetteWgsl = getCompiledShader('postprocess/vignette');
    this.shader = ctx.createShader(vignetteWgsl, vignetteWgsl);
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'vignette-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'vignette-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      intensity: this.vignetteIntensity * intensity,
      smoothness: this.smoothness,
      roundness: this.roundness,
      _pad: 0,
      center: [this.centerX, this.centerY],
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
